import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { 
  User, 
  FileText, 
  Briefcase, 
  GraduationCap, 
  Heart, 
  Plus, 
  Trash2,
  Type,
  Layout,
  Languages,
  Globe,
  Quote,
  Sparkles,
  AlignLeft
} from "lucide-react";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { CVData, WorkEntry, EducationEntry } from "@/lib/cvParser";

function debounce<T extends (...args: any[]) => void>(func: T, wait: number) {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

const DebouncedInput = memo(({ value, onUpdate, ...props }: any) => {
  const [localValue, setLocalValue] = useState(value);
  const debouncedUpdate = useRef(debounce(onUpdate, 500));

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
    debouncedUpdate.current(e.target.value);
  };

  return <Input {...props} value={localValue} onChange={handleChange} />;
});

const DebouncedTextarea = memo(({ value, onUpdate, ...props }: any) => {
  const [localValue, setLocalValue] = useState(value);
  const debouncedUpdate = useRef(debounce(onUpdate, 500));

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalValue(e.target.value);
    debouncedUpdate.current(e.target.value);
  };

  return <Textarea {...props} value={localValue} onChange={handleChange} />;
});

// --- Memoized Sections ---

const PersonalDetailsSection = memo(({ data, updateField, addItemToList, updateListField, removeItemFromList }: any) => (
  <AccordionItem value="personal-details" className="border-border px-6 flex-1 flex flex-col">
    <AccordionTrigger className="hover:no-underline py-5 group">
      <div className="flex items-center gap-3">
        <User className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
        <span className="text-sm font-semibold text-foreground/80 group-hover:text-foreground transition-colors">Personal Details</span>
      </div>
    </AccordionTrigger>
    <AccordionContent className="space-y-4">
      <div className="space-y-2">
        <Label className="text-[11px] text-muted-foreground uppercase font-bold tracking-wider">Full Name</Label>
        <DebouncedInput 
          value={data.name} 
          onUpdate={(val: string) => updateField("name", val)}
          className="bg-card/50 border-border text-foreground focus:ring-primary/20"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-[11px] text-muted-foreground uppercase font-bold tracking-wider">Headline / Professional Title</Label>
        <DebouncedInput 
          value={data.jobTitle} 
          onUpdate={(val: string) => updateField("jobTitle", val)}
          className="bg-card/50 border-border text-foreground focus:ring-primary/20"
          placeholder="e.g. Senior Product Designer"
        />
      </div>
      <div className="space-y-3">
        <Label className="text-[11px] text-muted-foreground uppercase font-bold tracking-wider flex justify-between items-center">
          Contact & Links
          <Button variant="ghost" size="icon" className="h-4 w-4 text-muted-foreground hover:text-primary" onClick={() => addItemToList("basicInfo")}>
            <Plus className="h-3 w-3" />
          </Button>
        </Label>
        {data.basicInfo.map((info: string, i: number) => {
          const placeholders = ["Name", "Email", "Number", "Location"];
          return (
            <div key={i} className="flex gap-2 group/item">
              <DebouncedInput 
                value={info} 
                onUpdate={(val: string) => updateListField("basicInfo", i, val)}
                className="bg-card/50 border-border text-foreground text-xs"
                placeholder={placeholders[i] || "Add contact detail..."}
              />
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-9 w-9 text-muted-foreground hover:text-red-400 opacity-0 group-hover/item:opacity-100 transition-opacity"
              onClick={() => removeItemFromList("basicInfo", i)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
            </div>
          );
        })}
      </div>
    </AccordionContent>
  </AccordionItem>
));

const ProfileSection = memo(({ data, updateField }: any) => (
  <AccordionItem value="profile" className="border-border px-6 flex-1 flex flex-col">
    <AccordionTrigger className="hover:no-underline py-5 group">
      <div className="flex items-center gap-3">
        <AlignLeft className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
        <span className="text-sm font-semibold text-foreground/80 group-hover:text-foreground transition-colors">Profile</span>
      </div>
    </AccordionTrigger>
    <AccordionContent className="space-y-4">
      <div className="space-y-2">
        <Label className="text-[11px] text-muted-foreground uppercase font-bold tracking-wider">Professional Summary</Label>
        <DebouncedTextarea 
          value={data.profile} 
          onUpdate={(val: string) => updateField("profile", val)}
          className="bg-card/50 border-border text-foreground text-xs min-h-[150px] leading-relaxed"
        />
      </div>
    </AccordionContent>
  </AccordionItem>
));

const SkillsSection = memo(({ data, updateListField, removeItemFromList, addItemToList }: any) => (
  <AccordionItem value="skills" className="border-border px-6 flex-1 flex flex-col">
    <AccordionTrigger className="hover:no-underline py-5 group">
      <div className="flex items-center gap-3">
        <Sparkles className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
        <span className="text-sm font-semibold text-foreground/80 group-hover:text-foreground transition-colors">Core Skills</span>
      </div>
    </AccordionTrigger>
    <AccordionContent className="space-y-3">
      {data.keySkills.map((skill: string, i: number) => (
        <div key={i} className="flex gap-2 group/item">
          <DebouncedInput 
            value={skill} 
            onUpdate={(val: string) => updateListField("keySkills", i, val)}
            className="bg-card/50 border-border text-foreground text-xs"
          />
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-9 w-9 text-muted-foreground hover:text-red-400 opacity-0 group-hover/item:opacity-100 transition-opacity flex-shrink-0"
            onClick={() => removeItemFromList("keySkills", i)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button 
        variant="outline" 
        size="sm" 
        className="w-full mt-2 border-dashed border-border text-muted-foreground hover:bg-primary/10 hover:text-primary hover:border-primary/50 text-[11px] uppercase font-bold tracking-wider"
        onClick={() => addItemToList("keySkills")}
      >
        <Plus className="h-3 w-3 mr-2" /> Add Skill
      </Button>
    </AccordionContent>
  </AccordionItem>
));

const ExperienceSection = memo(({ data, updateWorkExperience, removeWorkExperience, addWorkExperience, addWorkBullet, updateWorkBullet, removeWorkBullet }: any) => (
  <AccordionItem value="experience" className="border-border px-6 flex-1 flex flex-col">
    <AccordionTrigger className="hover:no-underline py-5 group">
      <div className="flex items-center gap-3">
        <Briefcase className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
        <span className="text-sm font-semibold text-foreground/80 group-hover:text-foreground transition-colors">Professional Experience</span>
      </div>
    </AccordionTrigger>
    <AccordionContent className="space-y-6 pt-2">
      {data.workExperience.map((work: WorkEntry, i: number) => (
        <div key={i} className="space-y-4 p-4 rounded-xl bg-card border border-border/50 relative group/card">
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute top-2 right-2 h-7 w-7 text-muted-foreground hover:text-red-400 opacity-0 group-hover/card:opacity-100 transition-opacity"
            onClick={() => removeWorkExperience(i)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
          
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Job Title</Label>
                <DebouncedInput 
                  value={work.jobTitle} 
                  onUpdate={(val: string) => updateWorkExperience(i, "jobTitle", val)}
                  className="bg-background/50 border-border text-foreground text-xs h-8"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Dates</Label>
                <DebouncedInput 
                  value={work.dates} 
                  onUpdate={(val: string) => updateWorkExperience(i, "dates", val)}
                  className="bg-background/50 border-border text-foreground text-xs h-8"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Company</Label>
              <DebouncedInput 
                value={work.company} 
                onUpdate={(val: string) => updateWorkExperience(i, "company", val)}
                className="bg-background/50 border-border text-foreground text-xs h-8"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider flex justify-between items-center">
                Achievements
                <Button variant="ghost" size="icon" className="h-4 w-4 text-muted-foreground hover:text-primary" onClick={() => addWorkBullet(i)}>
                  <Plus className="h-3 w-3" />
                </Button>
              </Label>
              <div className="space-y-2">
                {work.bullets.map((bullet, bi) => (
                  <div key={bi} className="flex gap-2 group/bullet">
                    <DebouncedTextarea 
                      value={bullet} 
                      onUpdate={(val: string) => updateWorkBullet(i, bi, val)}
                      className="bg-background/50 border-border text-foreground text-[11px] min-h-[50px] leading-relaxed py-2"
                    />
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-7 w-7 text-muted-foreground/50 hover:text-red-400 opacity-0 group-hover/bullet:opacity-100 transition-opacity mt-1 flex-shrink-0"
                      onClick={() => removeWorkBullet(i, bi)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
      <Button 
        variant="outline" 
        size="sm" 
        className="w-full border-dashed border-border text-muted-foreground hover:bg-primary/10 hover:text-primary hover:border-primary/50 text-[11px] uppercase font-bold tracking-wider py-5"
        onClick={addWorkExperience}
      >
        <Plus className="h-3.5 w-3.5 mr-2" /> Add Experience
      </Button>
    </AccordionContent>
  </AccordionItem>
));

const EducationSection = memo(({ data, updateEducation, removeEducation, addEducation }: any) => (
  <AccordionItem value="education" className="border-border px-6 flex-1 flex flex-col">
    <AccordionTrigger className="hover:no-underline py-5 group">
      <div className="flex items-center gap-3">
        <GraduationCap className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
        <span className="text-sm font-semibold text-foreground/80 group-hover:text-foreground transition-colors">Education</span>
      </div>
    </AccordionTrigger>
    <AccordionContent className="space-y-6 pt-2">
      {data.education.map((edu: EducationEntry, i: number) => (
        <div key={i} className="space-y-4 p-4 rounded-xl bg-card border border-border/50 relative group/card">
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute top-2 right-2 h-7 w-7 text-muted-foreground hover:text-red-400 opacity-0 group-hover/card:opacity-100 transition-opacity"
            onClick={() => removeEducation(i)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
          
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Qualification</Label>
                <DebouncedInput 
                  value={edu.qualification} 
                  onUpdate={(val: string) => updateEducation(i, "qualification", val)}
                  className="bg-background/50 border-border text-foreground text-xs h-8"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Dates</Label>
                <DebouncedInput 
                  value={edu.dates} 
                  onUpdate={(val: string) => updateEducation(i, "dates", val)}
                  className="bg-background/50 border-border text-foreground text-xs h-8"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Institution</Label>
              <DebouncedInput 
                value={edu.institution} 
                onUpdate={(val: string) => updateEducation(i, "institution", val)}
                className="bg-background/50 border-border text-foreground text-xs h-8"
              />
            </div>
          </div>
        </div>
      ))}
      <Button 
        variant="outline" 
        size="sm" 
        className="w-full border-dashed border-border text-muted-foreground hover:bg-primary/10 hover:text-primary hover:border-primary/50 text-[11px] uppercase font-bold tracking-wider py-5"
        onClick={addEducation}
      >
        <Plus className="h-3.5 w-3.5 mr-2" /> Add Education
      </Button>
    </AccordionContent>
  </AccordionItem>
));

const InterestsSection = memo(({ data, updateListField, removeItemFromList, addItemToList }: any) => (
  <AccordionItem value="interests" className="border-border px-6 flex-1 flex flex-col">
    <AccordionTrigger className="hover:no-underline py-5 group">
      <div className="flex items-center gap-3">
        <Heart className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
        <span className="text-sm font-semibold text-foreground/80 group-hover:text-foreground transition-colors">Interests</span>
      </div>
    </AccordionTrigger>
    <AccordionContent className="space-y-3">
      {data.interests.map((item: string, i: number) => (
        <div key={i} className="flex gap-2 group/item">
          <DebouncedInput 
            value={item} 
            onUpdate={(val: string) => updateListField("interests", i, val)}
            className="bg-card/50 border-border text-foreground text-xs"
          />
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-9 w-9 text-muted-foreground hover:text-red-400 opacity-0 group-hover/item:opacity-100 transition-opacity flex-shrink-0"
            onClick={() => removeItemFromList("interests", i)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button 
        variant="outline" 
        size="sm" 
        className="w-full mt-2 border-dashed border-border text-muted-foreground hover:bg-primary/10 hover:text-primary hover:border-primary/50 text-[11px] uppercase font-bold tracking-wider"
        onClick={() => addItemToList("interests")}
      >
        <Plus className="h-3 w-3 mr-2" /> Add Interest
      </Button>
    </AccordionContent>
  </AccordionItem>
));

const ReferencesSection = memo(({ data, updateField }: any) => (
  <AccordionItem value="references" className="border-border px-6 flex-1 flex flex-col">
    <AccordionTrigger className="hover:no-underline py-5 group">
      <div className="flex items-center gap-3">
        <Quote className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
        <span className="text-sm font-semibold text-foreground/80 group-hover:text-foreground transition-colors">References</span>
      </div>
    </AccordionTrigger>
    <AccordionContent className="space-y-4">
      <div className="space-y-2">
        <Label className="text-[11px] text-muted-foreground uppercase font-bold tracking-wider">Reference Info</Label>
        <DebouncedTextarea 
          value={data.references} 
          onUpdate={(val: string) => updateField("references", val)}
          className="bg-card/50 border-border text-foreground text-xs min-h-[80px]"
          placeholder="References available upon request..."
        />
      </div>
    </AccordionContent>
  </AccordionItem>
));

interface CVEditorSidebarProps {
  data: CVData;
  onChange: (newData: CVData) => void;
}

export const CVEditorSidebar = memo(({ data, onChange }: CVEditorSidebarProps) => {
  if (!data) return null;

  const updateField = useCallback((field: keyof CVData, value: any) => {
    onChange({ ...data, [field]: value });
  }, [data, onChange]);

  const updateWorkExperience = useCallback((index: number, field: keyof WorkEntry, value: any) => {
    const newExperience = [...data.workExperience];
    newExperience[index] = { ...newExperience[index], [field]: value };
    updateField("workExperience", newExperience);
  }, [data.workExperience, updateField]);

  const addWorkExperience = useCallback(() => {
    const newExperience = [
      ...data.workExperience,
      { jobTitle: "New Position", dates: "Date - Present", company: "Company Name", bullets: ["Key responsibility or achievement"] }
    ];
    updateField("workExperience", newExperience);
  }, [data.workExperience, updateField]);

  const removeWorkExperience = useCallback((index: number) => {
    const newExperience = data.workExperience.filter((_, i) => i !== index);
    updateField("workExperience", newExperience);
  }, [data.workExperience, updateField]);

  const updateEducation = useCallback((index: number, field: keyof EducationEntry, value: any) => {
    const newEducation = [...data.education];
    newEducation[index] = { ...newEducation[index], [field]: value };
    updateField("education", newEducation);
  }, [data.education, updateField]);

  const addEducation = useCallback(() => {
    const newEducation = [
      ...data.education,
      { qualification: "Degree Name", dates: "Year - Year", institution: "University Name", notes: [] }
    ];
    updateField("education", newEducation);
  }, [data.education, updateField]);

  const removeEducation = useCallback((index: number) => {
    const newEducation = data.education.filter((_, i) => i !== index);
    updateField("education", newEducation);
  }, [data.education, updateField]);

  const updateWorkBullet = useCallback((workIndex: number, bulletIndex: number, value: string) => {
    const newExperience = [...data.workExperience];
    const newBullets = [...newExperience[workIndex].bullets];
    newBullets[bulletIndex] = value;
    newExperience[workIndex] = { ...newExperience[workIndex], bullets: newBullets };
    updateField("workExperience", newExperience);
  }, [data.workExperience, updateField]);

  const addWorkBullet = useCallback((workIndex: number) => {
    const newExperience = [...data.workExperience];
    newExperience[workIndex] = { 
      ...newExperience[workIndex], 
      bullets: [...newExperience[workIndex].bullets, "New achievement..."] 
    };
    updateField("workExperience", newExperience);
  }, [data.workExperience, updateField]);

  const removeWorkBullet = useCallback((workIndex: number, bulletIndex: number) => {
    const newExperience = [...data.workExperience];
    newExperience[workIndex] = { 
      ...newExperience[workIndex], 
      bullets: newExperience[workIndex].bullets.filter((_, i) => i !== bulletIndex) 
    };
    updateField("workExperience", newExperience);
  }, [data.workExperience, updateField]);

  const updateListField = useCallback((field: "basicInfo" | "keySkills" | "interests", index: number, value: string) => {
    const newList = [...data[field]];
    newList[index] = value;
    updateField(field, newList);
  }, [data, updateField]);

  const addItemToList = useCallback((field: "basicInfo" | "keySkills" | "interests") => {
    updateField(field, [...data[field], "New item..."]);
  }, [data, updateField]);

  const removeItemFromList = useCallback((field: "basicInfo" | "keySkills" | "interests", index: number) => {
    updateField(field, data[field].filter((_, i) => i !== index));
  }, [data, updateField]);

  return (
    <div className="flex flex-col h-full bg-background border-r border-border w-full sm:w-[380px] lg:w-[450px] flex-shrink-0 overflow-y-auto custom-scrollbar">
      <div className="p-6 border-b border-border bg-card/50 flex items-center justify-between sticky top-0 z-10 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center border border-primary/30">
            <Layout className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-foreground uppercase tracking-tight">Editor</h2>
            <p className="text-[10px] text-muted-foreground font-medium tracking-wide uppercase">Real-time Customization</p>
          </div>
        </div>
      </div>

      <Accordion type="single" collapsible className="w-full flex-1 flex flex-col">
        <PersonalDetailsSection 
          data={data} 
          updateField={updateField} 
          addItemToList={addItemToList} 
          updateListField={updateListField} 
          removeItemFromList={removeItemFromList} 
        />
        <ProfileSection data={data} updateField={updateField} />
        <SkillsSection 
          data={data} 
          updateListField={updateListField} 
          removeItemFromList={removeItemFromList} 
          addItemToList={addItemToList} 
        />
        <ExperienceSection 
          data={data} 
          updateWorkExperience={updateWorkExperience} 
          removeWorkExperience={removeWorkExperience} 
          addWorkExperience={addWorkExperience} 
          addWorkBullet={addWorkBullet} 
          updateWorkBullet={updateWorkBullet} 
          removeWorkBullet={removeWorkBullet} 
        />
        <EducationSection 
          data={data} 
          updateEducation={updateEducation} 
          removeEducation={removeEducation} 
          addEducation={addEducation} 
        />
        <InterestsSection 
          data={data} 
          updateListField={updateListField} 
          removeItemFromList={removeItemFromList} 
          addItemToList={addItemToList} 
        />
        <ReferencesSection data={data} updateField={updateField} />
      </Accordion>
      
      <div className="mt-auto p-6 border-t border-border bg-card/30">
        <p className="text-[10px] text-muted-foreground leading-relaxed text-center italic">
          Your changes are automatically saved and reflected in the preview on the right.
        </p>
      </div>
    </div>
  );
});
